import { z } from 'zod'
import { deliveryFailReasons } from './tracking'
import { type WaybillItem, type WaybillValues, waybillOwed } from './waybill'

/**
 * Sending a bill back to its sender (ตีกลับ): the receiver refused it, or it cannot be delivered. A bill on its way —
 * wherever the system still has it, since not every move is scanned: the goods are where the person says (`branchId`),
 * and a bill on a truck or a run comes off it there first; not one still at the origin it never left, which is
 * cancelled instead — becomes `returned`, and its goods go on as a new waybill linked both ways (`returnId`, `returnOfId`): from
 * that branch back to the original's origin — to the sender's door when we collected from them (`pickup`), to that
 * branch's counter when they brought it (`dropoff`) — sender and receiver swapped, the goods copied, paid by its receiver
 * (the original sender). Its freight is what the branch head enters, plus
 * what the receiver still owed on the original (`returnMove`), which then leaves the original. Cancelling the return
 * bill (only before it has moved) puts the original back as it was.
 */
export const returnInput = z
  .object({
    reason: z.enum(deliveryFailReasons),
    note: z.string().trim().max(500).optional(),
    /** The return freight the original sender pays; 0 when the company carries it back free */
    freight: z.number().min(0),
    /** Where the goods are, when the system has them elsewhere: one's own branch for a branch lead */
    branchId: z.string().min(1).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.reason === 'other' && !value.note) ctx.addIssue({ code: 'custom', path: ['note'], message: 'ระบุเหตุผลที่ตีกลับ' })
  })
export type ReturnInput = z.infer<typeof returnInput>

/**
 * Not delivered, recorded from the bill (ส่งไม่สำเร็จ) rather than its run: why, and where the goods are now — they wait
 * there for another run. A bill out on a run is failed on it, as its run would
 */
export const notDeliveredInput = z
  .object({
    reason: z.enum(deliveryFailReasons),
    note: z.string().trim().max(500).optional(),
    branchId: z.string().min(1).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.reason === 'other' && !value.note)
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'ใส่รายละเอียดว่าส่งไม่สำเร็จเพราะอะไร' })
  })
export type NotDeliveredInput = z.infer<typeof notDeliveredInput>

/**
 * What the receiver of a bill sent back still owes, moved onto the return bill (the sender pays it when the goods come
 * back); what anyone already paid stays where it is
 */
export function returnMove(bill: WaybillValues): number {
  return waybillOwed(bill).receiver
}

const partyFields = ['Id', 'Name', 'Phone', 'Address', 'Subdistrict', 'District', 'Province', 'Postcode', 'BusinessType'] as const

/** The return bill's two charge lines: its own freight, and the outbound freight it carries ("ค่าขนส่งขาไป บิล {{number}}") */
export interface ReturnLineNames {
  freight: string
  carried: string
}

/**
 * The return bill of `bill`, as waybill input: booked `at` the branch it stands at, back to its origin, the parties
 * swapped, its goods copied at no rate, and the freight as charge lines (`freight` entered, `moved` from the original)
 */
export function returnBillOf(
  bill: WaybillValues,
  at: { branchId: string; bookedAt: string; freight: number; moved: number; notes: string },
  names: ReturnLineNames,
) {
  const swapped = (from: 'sender' | 'receiver', to: 'sender' | 'receiver') =>
    Object.fromEntries(partyFields.map((field) => [`${to}${field}`, bill[`${from}${field}`] ?? null]))
  const goods = ((bill.items ?? []) as WaybillItem[])
    .filter((item) => !item.charge)
    .map(({ productId, name, qty, unit, weightKg }) => ({
      productId: productId ?? null,
      name,
      qty,
      unit: unit ?? null,
      weightKg: weightKg ?? null,
      rate: 0,
    }))
  const charge = (name: string, rate: number) => (rate > 0 ? [{ name, qty: 1, rate, charge: true }] : [])
  return {
    bookedAt: at.bookedAt,
    payer: 'receiver' as const,
    originBranchId: at.branchId,
    originMode: 'dropoff' as const,
    ...swapped('receiver', 'sender'),
    destMode: bill.originMode === 'pickup' ? ('door' as const) : ('branch' as const),
    destBranchId: String(bill.originBranchId),
    ...swapped('sender', 'receiver'),
    items: [
      ...goods,
      ...charge(names.freight, at.freight),
      ...charge(names.carried.replace('{{number}}', String(bill.number ?? '')), at.moved),
    ],
    notes: at.notes,
  }
}
